/**
 * Session storage utilities for tracking interstitial states
 */

type InterstitialState = 'skipped' | 'completed';

const STORAGE_KEYS = {
  skipped: 'eluve_interstitials_skipped',
  completed: 'eluve_interstitials_completed',
} as const;

/**
 * Mark an interstitial with the given state in this session
 */
const markInterstitialAs = (
  flowName: string,
  state: InterstitialState,
): void => {
  try {
    const current = getInterstitialState(state);
    current[flowName] = true;
    sessionStorage.setItem(STORAGE_KEYS[state], JSON.stringify(current));
  } catch {
    // Silently fail - not critical for app functionality
  }
};

/**
 * Check if an interstitial has the given state in this session
 */
const isInterstitialMarkedAs = (
  flowName: string,
  state: InterstitialState,
): boolean => {
  try {
    const current = getInterstitialState(state);
    return current[flowName] === true;
  } catch {
    return false;
  }
};

/**
 * Get all interstitials with the given state from session storage
 */
const getInterstitialState = (
  state: InterstitialState,
): Record<string, boolean> => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEYS[state]);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

// Public API - maintain existing function names for compatibility
export const markInterstitialAsSkipped = (flowName: string): void =>
  markInterstitialAs(flowName, 'skipped');

export const markInterstitialAsCompleted = (flowName: string): void =>
  markInterstitialAs(flowName, 'completed');

export const isInterstitialSkippedInSession = (flowName: string): boolean =>
  isInterstitialMarkedAs(flowName, 'skipped');

export const isInterstitialCompletedInSession = (flowName: string): boolean =>
  isInterstitialMarkedAs(flowName, 'completed');

/**
 * Clear all interstitial states
 */
export const clearSkippedInterstitials = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEYS.skipped);
    sessionStorage.removeItem(STORAGE_KEYS.completed);
  } catch {
    // Silently fail
  }
};
